import { DEFAULT_LANG, Language, languageLabels } from "../i18n/languages";
import { ApiService, ShareholderNotification } from "../api";
import AlertMessageStore from "./AlertMessageStore";
import { makeAutoObservable } from "mobx";
import { ExtendedShareholderDTO } from "../types/Shareholder";
import { partition } from "lodash";
import { CompanyItem, emailValidator } from "../common/common";
import { OptionFieldModel, RadioFieldModel, StringFieldModel } from "../common/commonForms";
import { hasShares } from "./ShareholdersStore";
import { User } from "../types/UserInfo";
import { TMessage } from "../i18n/tt";
import { getFullName } from "../utils/Roles";
import { AIClient } from "../ai/AIAssistant";
import { LocalizedTextEditorModel } from "../text/localizedTextEditorDomain";

export class NotifyShareholdersStore {
  isNotifyingShareholders = false;
  shareholders?: OptionFieldModel<NotifyShareholderEmailModel>;
  notifyShareholdersEmailLanguage: RadioFieldModel<Language>;
  user: User | null;
  isPersonalNoteEnabled: boolean = false;
  personalNote: LocalizedTextEditorModel;

  constructor(
    readonly apiService: ApiService,
    readonly messageStore: AlertMessageStore,
    readonly companyItem: CompanyItem,
    user: User | null,
    aiClient: AIClient,
  ) {
    this.companyItem = companyItem;
    this.notifyShareholdersEmailLanguage = new RadioFieldModel<Language>({
      value: DEFAULT_LANG,
      options: [Language.EN, Language.NO],
      idFn: (l) => l,
      labelFn: (l) => ({
        key: languageLabels[l],
      }),
    });
    this.user = user;
    this.personalNote = new LocalizedTextEditorModel({
      texts: [{ text: "", language: Language.NO, translationOption: { autoTranslate: false } }],
      isReadOnly: () => false,
      label: { translated: "" },
      aiClient,
    });
    makeAutoObservable(this, { apiService: false, messageStore: false }, { autoBind: true });
  }

  get generateCommonEmailBody(): TMessage {
    return {
      key: "invite-user.email.default-text",
      params: {
        senderName: getFullName(this.user),
        email: this.user?.email,
        companyName: this.companyItem.companyName,
      },
    };
  }

  get isSubmitDisabled(): boolean | undefined {
    return (
      this.shareholders?.isNoneSelected ||
      this.shareholders?.selected.some((f) => !f.email.isValid || f.email.isEmpty)
    );
  }

  togglePersonalNote() {
    this.isPersonalNoteEnabled = !this.isPersonalNoteEnabled;
    if (!this.isPersonalNoteEnabled && this.personalNote?.textModels?.length > 0) {
      this.personalNote.textModels[0].model.setAndCommitInputValue("");
    }
  }

  *notifyShareholders() {
    if (this.isNotifyingShareholders) {
      return;
    }
    this.isNotifyingShareholders = true;
    try {
      const shareholders =
        this.shareholders?.selected
          ?.map((s) => s.toShareholderNotification())
          .filter((s) => s.email) ?? [];
      if (shareholders.length > 0) {
        yield this.apiService.inviteShareholders(
          this.companyItem.organisationNumber,
          shareholders,
          this.notifyShareholdersEmailLanguage.value,
          this.isPersonalNoteEnabled ? this.personalNote.textModels[0].model.inputValue : undefined,
        );
      }
      this.messageStore.showSuccess({ key: "shareholders.notify.all.success" }, { autoHide: true });
    } catch (e) {
      this.messageStore.showError({ key: "shareholders.notify.all.error" });
    } finally {
      this.isNotifyingShareholders = false;
    }
  }

  initShareholdersToNotify(
    shareholders: ExtendedShareholderDTO[],
    selectedShareholderIds?: string[],
  ) {
    const shareholdersWithShares = shareholders.filter((s) => hasShares(s));
    this.shareholders = createNotifyShareholdersModel(shareholdersWithShares);
    if (selectedShareholderIds !== undefined) {
      this.shareholders.toggleAll(false);
      const updateForModel = (id: string, model: OptionFieldModel<NotifyShareholderEmailModel>) => {
        const option = model.options.find((s) => s.id === id);
        if (option) {
          model.select(option);
        }
      };
      for (const id of selectedShareholderIds) {
        updateForModel(id, this.shareholders);
      }
    }
  }
}

function createNotifyShareholdersModel(shareholders: ExtendedShareholderDTO[]) {
  const [shareholdersWithEmail, shareholdersWithoutEmail] = partition(shareholders, (s) => s.email);
  return new OptionFieldModel<NotifyShareholderEmailModel>({
    options: [...shareholdersWithEmail, ...shareholdersWithoutEmail].map(
      (s) => new NotifyShareholderEmailModel(s),
    ),
    idFn: (s) => s.id,
    labelFn: (s) => s.label,
    selectedIds: shareholdersWithEmail.map((s) => String(s.id)),
  });
}

export class NotifyShareholderEmailModel {
  readonly email: StringFieldModel;

  constructor(readonly shareholder: ExtendedShareholderDTO) {
    this.email = new StringFieldModel({
      value: shareholder.email,
      required: true,
      validator: emailValidator,
    });
    makeAutoObservable(this, { shareholder: false }, { autoBind: true });
  }

  get id() {
    return String(this.shareholder.id);
  }

  get label() {
    return {
      translated: this.shareholder.name,
    };
  }

  toShareholderNotification(): ShareholderNotification {
    return {
      shareholderId: this.shareholder.id,
      email: this.email.validatedValue,
    };
  }

  get name() {
    return this.shareholder.name;
  }
}
